/**
 * File: bfwt-deals.js (Vanilla JS - Complete & Modular Version)
 * Purpose: Manages user click events on deal elements and triggers popups.
 * Architecture: Pure event-driven, no async operations before link opening
 */
import './deals-base.css';
import './deals-cta.css';

(function () {
  'use strict';

  // Deal type enumeration
  const DEAL_TYPES = {
    APP_ONLY: 'app-only',
    STANDARD: 'standard',
  };

  let isProcessingClick = false;

  // --- UTILITY FUNCTIONS ---

  const getUrlParameter = (name) => {
    const params = new URLSearchParams(window.location.search);
    return params.get(name) || '';
  };

  /**
   * Determine deal type based on theme and flags
   * @param {Object} dealData - Deal dataset from HTML
   * @returns {string} Deal type constant
   */
  const getDealType = (dealData) => {
    const isAppOnly =
      dealData['app-only'] === 'true' ||
      dealData['app-only'] === true ||
      dealData.appOnly === 'true' ||
      dealData.appOnly === true;
    if (isAppOnly) return DEAL_TYPES.APP_ONLY;

    return DEAL_TYPES.STANDARD;
  };

  /**
   * Get device-specific affiliate link
   * @param {Object} linkData - Object with ctalink, iosLink, androidLink
   * @returns {string} Final URL to open
   */
  /**
   * Get device-specific affiliate link
   * @param {Object} linkData - Object with ctalink, iosLink, androidLink
   * @param {Element} [btn] - CTA element clicked
   * @returns {string} Final URL to open
   */
  const getDeviceSpecificLink = (linkData, btn) => {
    let ctaLink = linkData.ctalink || linkData.ctaLink;
    if ((!ctaLink || ctaLink === '#') && btn) {
      ctaLink = btn.getAttribute('data-ctalink') || btn.getAttribute('href');
    }
    return ctaLink || '#';
  };

  // --- CORE EVENT HANDLERS ---

  /**
   * Handle deal button clicks - route to correct flow based on deal type
   * IMPORTANT: All direct link opens happen here BEFORE any async operations
   * to avoid popup blocker interference
   */
  const handleDealClick = (event, btnTarget = null) => {
    const btn = btnTarget || event.currentTarget;

    // Allow default behavior for actual links unless handled
    // But for our buttons, we usually want to control the flow
    // event.preventDefault(); // Moved inside specific logic blocks if needed

    if (isProcessingClick) {
      event.preventDefault();
      return;
    }
    isProcessingClick = true;

    try {
      const row = btn.closest('.bfwt-deal-row-inner');
      if (!row) return;

      const dealData = row.dataset;
      const dealType = getDealType(dealData);
      const dealId = dealData.dealid;


      // === APP-ONLY: Show popup ===
      if (dealType === DEAL_TYPES.APP_ONLY) {
        event.preventDefault();
        const dealDataForPopup = {
          dealid: dealId,
          shorttitle: dealData.shorttitle,
          'brand-name': dealData.brandName,
          'brand-slug': dealData.brandSlug,
        };
        window.BFWT_Popups.createAndShowAppOnlyPopup(dealDataForPopup);
        return;
      }

      // === MEMBERS-ONLY: Show login sheet if not logged in ===
      const isMembersOnly =
        dealData['members-only'] === 'true' ||
        dealData['members-only'] === true ||
        dealData.membersOnly === 'true' ||
        dealData.membersOnly === true;

      const isLoggedIn = document.cookie.includes(
        'wordpress_logged_in_firebase=1'
      );

      if (isMembersOnly && !isLoggedIn) {
        event.preventDefault();
        if (
          window.BFWT_Membership &&
          typeof window.BFWT_Membership.openAuthModal === 'function'
        ) {
          window.BFWT_Membership.openAuthModal('register', {
            trigger: 'claim_deal_lock',
            brand_name: dealData.brandName || dealData['brand-name'] || '',
            brand_slug: dealData.brandSlug || dealData['brand-slug'] || '',
            item_title: dealData.shorttitle || dealData.title || '',
            deal_id: dealId || '',
          });
          return;
        }
        const accountSheet = document.getElementById('accountSheet');
        const trigger = document.getElementById('bfwt-account-trigger');
        if (accountSheet && window.sheetManager) {
          window.sheetManager.open(accountSheet, trigger);
        }
        return;
      }

      // === STANDARD DEAL & EXPERIENCE: Copy & Go / Activate ===
      event.preventDefault();

      // Inspect ad campaign parameters from URL or sessionStorage for dynamic subID tagging
      let campaignTag = '';
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const rawCamp =
          urlParams.get('utm_campaign') ||
          urlParams.get('campaign') ||
          (window.sessionStorage && window.sessionStorage.getItem('bf_utm_campaign')) ||
          '';
        if (rawCamp) {
          campaignTag = rawCamp.toLowerCase().replace(/[^a-z0-9_-]/g, '').substring(0, 16);
        }
      } catch (e) {}

      // Generate a deterministic universal click identifier for this conversion session
      const clickId =
        'bf_' +
        Date.now().toString(36) +
        '_' +
        Math.random().toString(36).substring(2, 7) +
        (campaignTag ? '_' + campaignTag : '');
      const rawPromo = (dealData.promo || dealData.code || dealData['promo-code'] || '').trim();
      const promoCodes = rawPromo.split(',').map((c) => c.trim()).filter(Boolean);
      const codeToCopy = promoCodes.length > 0 ? promoCodes[0] : '';

      const rawAffiliateLink = getDeviceSpecificLink(dealData, btn);

      // Pristine Affiliate Link Policy: Never mutate or append SubID/clickId parameters to outbound affiliate URLs.
      const affiliateLink = rawAffiliateLink;

      // Fire outbound telemetry on Tab 1 with this exact clickId
      if (typeof window.bfTrack === 'function') {
        window.bfTrack('affiliate_click', {
          event_category: 'monetization',
          bf_click_id: clickId,
          deal_id: dealId,
          deal_title: dealData.shorttitle || '',
          brand_name: dealData.brandName || '',
          brand_slug: dealData.brandSlug || '',
          outbound_url: affiliateLink,
          placement: 'deal_card_cta',
        });
      }

      // Open BudgetFitter companion page in NEW TAB with ?deal=<dealId>&bf_click_id=<clickId>&trigger=cta_companion
      try {
        const targetBaseUrl = dealData.brandUrl || dealData['brand-url'] || window.location.href;
        const currentUrl = new URL(targetBaseUrl, window.location.origin);
        currentUrl.searchParams.delete('reveal');
        if (dealId) {
          currentUrl.searchParams.set('deal', dealId);
          currentUrl.searchParams.set('bf_click_id', clickId);
          currentUrl.searchParams.set('trigger', 'cta_companion');
        }
        const newTab = window.open(currentUrl.toString(), '_blank');
        if (newTab) {
          newTab.opener = null;
        }
      } catch (e) {
        if (window.bf_debug) console.warn('Failed to open deal tab', e);
      }

      const isInternalRedirectRoute =
        Boolean(affiliateLink) && /\/(?:offer|visit|go|r|link|book)\//i.test(affiliateLink);

      // Only send client-side beacon when there is NO server-side /offer/ or /visit/ redirect route
      // (avoids duplicate logging, since OfferRedirector logs the authoritative 302 redirect row).
      if (!isInternalRedirectRoute) {
        try {
          const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
          const beaconPayload = JSON.stringify({
            session_id: clickId,
            deal_id: dealId || 0,
            brand_slug: dealData.brandSlug || '',
            network: dealData.network || '',
            event_type: 'deal_cta_click',
            destination_url: affiliateLink,
            promo_code: codeToCopy,
            client_device: isMobile ? 'mobile_web' : 'desktop_web',
            campaign_slug: campaignTag || '',
            gclid: (window.sessionStorage && window.sessionStorage.getItem('bf_gclid')) || '',
            fbclid: (window.sessionStorage && window.sessionStorage.getItem('bf_fbclid')) || '',
            ui_trigger_element: 'card_cta_btn',
          });

          const beaconUrl =
            (window.bf_api_settings && window.bf_api_settings.root ? window.bf_api_settings.root : '/wp-json/') +
            'app-api/v1/tracking/outbound-event';
          if (navigator.sendBeacon) {
            const blob = new Blob([beaconPayload], { type: 'application/json' });
            navigator.sendBeacon(beaconUrl, blob);
          } else {
            fetch(beaconUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: beaconPayload,
              keepalive: true,
            }).catch(() => {});
          }
        } catch (beaconErr) {}
      }

      // Navigate CURRENT TAB immediately to the internal /offer/{id}/ redirect route
      if (affiliateLink && affiliateLink !== '#') {
        let navTarget = affiliateLink;
        if (isInternalRedirectRoute) {
          try {
            const u = new URL(affiliateLink, window.location.origin);
            if (u.origin === window.location.origin) {
              u.searchParams.set('cid', clickId);
              navTarget = u.toString();
            }
          } catch (e) {}
        }
        window.location.href = navTarget;
      } else if (window.BFWT_Popups && typeof window.BFWT_Popups.showDealModal === 'function') {
        window.BFWT_Popups.showDealModal(dealData, { bf_click_id: clickId, trigger: 'cta_direct' });
      }
      return;
    } finally {
      setTimeout(() => {
        isProcessingClick = false;
      }, 300);
    }
  };

  async function handleFeedbackSubmission(module) {
    let reason = '';
    const activeOption = module.querySelector('.bfwt-feedback-option.active');

    if (activeOption) {
      // Using new options interface
      const val = activeOption.dataset.value;
      if (val === 'Other') {
        reason = 'Other: ' + module.querySelector('textarea').value.trim();
      } else {
        reason = val;
      }
    } else {
      // Fallback for old/simple layout (textarea only)
      reason = module.querySelector('textarea').value.trim();
    }

    if (
      !reason ||
      (activeOption &&
        activeOption.dataset.value === 'Other' &&
        !module.querySelector('textarea').value.trim())
    ) {
      if (module.querySelector('textarea:visible')) {
        module.querySelector('textarea').style.borderColor = 'red';
      }
      return;
    }

    const { dealId, dealTitle, brandName } = module.dataset;

    try {
      const restUrl = window.bfwtVars?.restUrl || '/wp-json/';
      const nonce = window.bfwtVars?.nonce || '';
      const response = await fetch(
        `${restUrl}app-api/v1/report-issue`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce,
          },
          body: JSON.stringify({
            deal_id: dealId,
            deal_title: dealTitle,
            brand_name: brandName,
            reason: reason,
            source: 'web',
          }),
        }
      );

      if (!response.ok) throw new Error('Submission failed');

      if (window.bfwt_trackEvent) {
        window.bfwt_trackEvent('feedback_form_submit', {
          deal_id: dealId || '',
          brand_name: brandName || '',
          reason: reason,
          event_category: 'engagement',
        });
      }

      module.querySelector('.bfwt-feedback-prompt').style.display = 'none';
      module.querySelector('.bfwt-feedback-form').style.display = 'none';
      const thankyou = module.querySelector('.bfwt-feedback-thankyou');
      thankyou.innerHTML =
        '<span class="material-symbols-rounded">check_circle</span> Report sent — we\'ll look into it!';
      thankyou.style.display = 'flex';
      // Auto-close the popup after 4 seconds
      const parentModal = module.closest('.bfwt-popup-overlay');
      if (parentModal && parentModal.closeModal) {
        setTimeout(() => parentModal.closeModal(), 4000);
      }
    } catch (error) {
      window.BFWT_Toast?.error('Sorry, your feedback could not be sent.');
      if (window.bf_debug) console.warn('Feedback Submission Error:', error);
    }
  }

  /**
   * Initialize client-side quick filter chips for the deals list
   */
  function initQuickFilters() {
    const filterContainer = document.querySelector('.bfwt-deal-filters');
    if (!filterContainer) return;

    const filterButtons = filterContainer.querySelectorAll(
      '.bfwt-deal-filter-btn'
    );
    const dealsContainer = document.querySelector('.bfwt-deals-container');
    if (!dealsContainer) return;

    const dealCards = dealsContainer.querySelectorAll('.bfwt-deal-card');

    filterButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        const selectedFilter = btn.dataset.filter;
        const isActive = btn.classList.contains('is-active');

        // Deactivate all filter buttons
        filterButtons.forEach((b) => b.classList.remove('is-active'));

        if (isActive || selectedFilter === 'all') {
          // If already active or clicked 'all', toggle off and show all deals
          const allBtn = filterContainer.querySelector('[data-filter="all"]');
          if (allBtn) {
            allBtn.classList.add('is-active');
          }

          dealCards.forEach((card) => {
            card.style.display = '';
            // Force reflow
            void card.offsetHeight;
            card.style.opacity = '1';
            card.style.transform = 'translateY(0)';
          });

          if (window.bfwt_trackEvent) {
            window.bfwt_trackEvent('deal_filter_reset', {
              event_category: 'bf_deals',
            });
          }
        } else {
          // Activate the clicked filter button
          btn.classList.add('is-active');

          // Filter deals with a smooth micro-animation
          dealCards.forEach((card) => {
            const cardType = card.dataset.dealType;

            if (cardType === selectedFilter) {
              card.style.display = '';
              // Force reflow
              void card.offsetHeight;
              card.style.opacity = '1';
              card.style.transform = 'translateY(0)';
            } else {
              card.style.opacity = '0';
              card.style.transform = 'translateY(8px)';

              // Hide from layout after animation completes
              const handleTransitionEnd = (evt) => {
                if (
                  evt.propertyName === 'opacity' &&
                  card.style.opacity === '0'
                ) {
                  card.style.display = 'none';
                  card.removeEventListener(
                    'transitionend',
                    handleTransitionEnd
                  );
                }
              };
              card.addEventListener('transitionend', handleTransitionEnd);

              // Fallback timeout in case transitionend doesn't fire
              setTimeout(() => {
                if (card.style.opacity === '0') {
                  card.style.display = 'none';
                }
              }, 250);
            }
          });

          if (window.bfwt_trackEvent) {
            window.bfwt_trackEvent('deal_filter_select', {
              filter_type: selectedFilter,
              event_category: 'bf_deals',
            });
          }
        }
      });
    });
  }

  // --- INITIALIZATION ---

  function initializePage() {
    // Quick Filters
    initQuickFilters();

    handleDealUrl();

    // Event Delegation for ALL deal and experience CTA buttons & icons on document.body
    document.body.addEventListener('click', (event) => {
      // Safety check: ensure target is an Element (not text node or document)
      if (!(event.target instanceof Element)) return;

      const ctaBtn = event.target.closest(
        '.bfwt-cta-button, .bfwt-code-container, .bfwt-app-only-button'
      );
      if (ctaBtn) {
        handleDealClick(event, ctaBtn);
        return;
      }

      const infoIcon = event.target.closest('.bfwt-info-icon');
      if (infoIcon) {
        event.preventDefault();
        const row = infoIcon.closest('.bfwt-deal-row-inner');
        if (row) window.BFWT_Popups.createAndShowInfoModal(row);
        return;
      }

      const watchlistIcon = event.target.closest('.bfwt-watchlist-icon');
      if (watchlistIcon) {
        event.preventDefault();
        const row = watchlistIcon.closest('.bfwt-deal-row-inner');
        if (row) {
          const dealData = row.dataset;
          if (window.bfwt_trackEvent) {
            window.bfwt_trackEvent('watchlist_add_open', {
              deal_id: dealData.dealid || '',
              brand_name: dealData.brandName || '',
              event_category: 'engagement',
            });
          }
          window.BFWT_Popups.createAndShowWatchlistPopup({
            dealid: dealData.dealid,
            'brand-name': dealData.brandName,
            'brand-slug': dealData.brandSlug,
          });
        }
        return;
      }

      // Report/Feedback Icon — opens the unified info modal jumped straight
      // to the Report tab. No separate report modal anymore.
      const reportIcon = event.target.closest('.bfwt-report-icon');
      if (reportIcon) {
        event.preventDefault();
        const row = reportIcon.closest('.bfwt-deal-row-inner');
        if (row) {
          if (window.bfwt_trackEvent) {
            window.bfwt_trackEvent('deal_report_open', {
              deal_id: row.dataset?.dealid || '',
              brand_name: row.dataset?.brandName || '',
              event_category: 'engagement',
            });
          }
          window.BFWT_Popups.createAndShowInfoModal(row, 'report');
        }
        return;
      }

      // Geri Bildirim Butonları (Olay Delegasyonu)
      const feedbackYes = event.target.closest('.bfwt-feedback-yes');
      if (feedbackYes) {
        const module = feedbackYes.closest('.bfwt-feedback-module');
        if (window.bfwt_trackEvent) {
          window.bfwt_trackEvent('vote_on_deal', {
            deal_id: module?.dataset?.dealId || '',
            feedback_vote: 'yes',
            event_category: 'engagement',
          });
        }
        module.querySelector('.bfwt-feedback-prompt').style.display = 'none';
        module.querySelector('.bfwt-feedback-buttons').style.display = 'none';
        const thankyou = module.querySelector('.bfwt-feedback-thankyou');
        thankyou.innerHTML =
          '<span class="material-symbols-rounded">favorite</span> Thanks for the feedback!';
        thankyou.style.display = 'flex';
        // Auto-close the popup after 3 seconds
        const parentModal = module.closest('.bfwt-popup-overlay');
        if (parentModal && parentModal.closeModal) {
          setTimeout(() => parentModal.closeModal(), 3000);
        }
        return;
      }

      const feedbackNo = event.target.closest('.bfwt-feedback-no');
      if (feedbackNo) {
        const module = feedbackNo.closest('.bfwt-feedback-module');
        if (window.bfwt_trackEvent) {
          window.bfwt_trackEvent('vote_on_deal', {
            deal_id: module?.dataset?.dealId || '',
            feedback_vote: 'no',
            event_category: 'engagement',
          });
        }
        const form = module.querySelector('.bfwt-feedback-form');
        form.style.display = 'block';
        feedbackNo.parentElement.style.display = 'none';
        return;
      }

      const feedbackSubmit = event.target.closest('.bfwt-feedback-submit');
      if (feedbackSubmit) {
        handleFeedbackSubmission(
          feedbackSubmit.closest('.bfwt-feedback-module')
        );
        return;
      }
    });

    // Body-level tooltip for .bfwt-tooltip elements
    setupBodyTooltip();

    // Listen to hash changes (e.g. user clicks in-article <a href="#deal-159219"> or search bar link)
    window.addEventListener('hashchange', () => {
      handleDealUrl();
    });
  }

  /**
   * Handle URL-based deal navigation:
   * 1. Query Params (?deal=ID or ?reveal=ID): Direct action/CTA intent -> smoothly scroll, pulse card, AND OPEN DEAL MODAL.
   * 2. Hash (#deal-ID) or Query Params (?highlight=ID or ?scroll=ID): Deep link / search result / in-article anchor -> smoothly scroll and pulse card ONLY (no modal).
   */
  function handleDealUrl() {
    // 1. Direct deal modal triggers (from CTA click / external direct modal link)
    const modalDealId = getUrlParameter('deal') || getUrlParameter('reveal');
    const clickId = getUrlParameter('bf_click_id') || '';
    const trigger = getUrlParameter('trigger') || 'direct';

    // 2. Scroll-only triggers (from search results, deep links, in-article mentions)
    let scrollDealId = getUrlParameter('highlight') || getUrlParameter('scroll');
    if (!scrollDealId && window.location.hash) {
      const match = window.location.hash.match(/#deal[-=_]?(\d+)/i);
      if (match) {
        scrollDealId = match[1];
      }
    }

    // Modal takes precedence if specified, otherwise scroll-only
    const dealId = modalDealId || scrollDealId;
    const shouldOpenModal = Boolean(modalDealId);

    if (dealId) {
      const row = document.querySelector(`.bfwt-deal-row-inner[data-dealid="${dealId}"]`);
      if (row) {
        // If the deal is inside an inactive tab pane, activate its tab first
        const tabPane = row.closest('.bfwt-tab-pane');
        if (tabPane && !tabPane.classList.contains('active') && tabPane.id) {
          const tabLink = document.querySelector(`.bfwt-custom-tabs a[href="#${tabPane.id}"]`);
          if (tabLink) {
            tabLink.click();
          }
        }

        const sliderTrack = row.closest('.bf-custom-slider__track');
        if (sliderTrack) {
          const slide = row.closest('.bf-custom-slider__slide');
          if (slide) {
            slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        } else {
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        row.classList.remove('bfwt-deal-highlight');
        void row.offsetWidth; // Trigger reflow so animation retriggers if clicked again
        row.classList.add('bfwt-deal-highlight');

        if (shouldOpenModal) {
          triggerDealModal(row.dataset, { bf_click_id: clickId, trigger: trigger });
        }
      }
    }
  }

  /**
   * Robust deal modal trigger with polling for BFWT_Popups availability
   */
  function triggerDealModal(dealData, options = {}) {
    if (!dealData) return;
    let attempts = 0;
    const maxAttempts = 30; // 3 seconds max (30 x 100ms)
    const interval = setInterval(() => {
      attempts++;
      if (window.BFWT_Popups && typeof window.BFWT_Popups.showDealModal === 'function') {
        clearInterval(interval);
        window.BFWT_Popups.showDealModal(dealData, options);
      } else if (window.BFWT_Popups && typeof window.BFWT_Popups.createAndShowCouponModal === 'function') {
        clearInterval(interval);
        window.BFWT_Popups.createAndShowCouponModal(dealData, options);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        console.warn('[BFWT Deals] BFWT_Popups module not available to display deal modal');
      }
    }, 100);
  }

  /**
   * Body-level tooltip — renders outside all card containers
   * to bypass backdrop-filter clipping contexts.
   * Touch-aware: hides on touch, scroll, and page visibility change.
   */
  function setupBodyTooltip() {
    const tip = document.createElement('div');
    tip.className = 'bfwt-body-tooltip';
    document.body.appendChild(tip);

    let isTouchDevice = false;

    const hideTip = () => {
      if (tip.classList.contains('show')) {
        tip.classList.remove('show');
        window.removeEventListener('scroll', hideTip);
      }
    };

    // Detect touch — suppress tooltip on touch interactions
    document.addEventListener(
      'touchstart',
      () => {
        isTouchDevice = true;
        hideTip();
      },
      { passive: true }
    );

    document.addEventListener(
      'mouseenter',
      (e) => {
        if (isTouchDevice) return; // Don't show tooltip on touch devices
        if (!(e.target instanceof Element)) return; // Safety check
        const target = e.target.closest('.bfwt-tooltip[data-tooltip]');
        if (!target) return;

        tip.textContent = target.getAttribute('data-tooltip');

        // Position above the element
        const rect = target.getBoundingClientRect();
        const tipWidth = tip.offsetWidth;
        let left = rect.left + rect.width / 2 - tipWidth / 2;

        // Keep within viewport
        if (left < 8) left = 8;
        if (left + tipWidth > window.innerWidth - 8)
          {left = window.innerWidth - 8 - tipWidth;}

        tip.style.left = left + 'px';
        tip.style.top = rect.top - tip.offsetHeight - 10 + 'px';
        tip.classList.add('show');

        // Dynamically listen to scroll only when tooltip is active
        window.addEventListener('scroll', hideTip, { passive: true });
      },
      true
    );

    document.addEventListener(
      'mouseleave',
      (e) => {
        if (!(e.target instanceof Element)) return; // Safety check
        const target = e.target.closest('.bfwt-tooltip[data-tooltip]');
        if (!target) return;
        hideTip();
      },
      true
    );

    // Hide on page visibility change
    document.addEventListener('visibilitychange', hideTip);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePage);
  } else {
    initializePage();
  }
})();
